import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTransactionSigner } from "../wallet/hooks/e2e/useTransactionSigner";

import { ClaimBrowserChain } from "./ClaimBrowserChain";
import { browserClaimStorage, ClaimFlow, type ClaimReview } from "./ClaimFlow";
import type { ClaimApi, ClaimRequest, ClaimRewards, ClaimWallet } from "./claimTypes";
interface UseClaimFlowResult {
    rewards: ClaimRewards | null;
    request: ClaimRequest | null;
    review: ClaimReview | null;
    busy: boolean;
    error: string | null;
    authorized: boolean;
    ready: boolean;
    load: () => Promise<void>;
    prepare: () => Promise<void>;
    submit: () => Promise<void>;
    claim: () => Promise<void>;
}

export function useClaimFlow(api: ClaimApi): UseClaimFlowResult {
    const { signer, address, ready } = useTransactionSigner();
    const walletRef = useRef({ signer, address, ready });
    walletRef.current = { signer, address, ready };
    const [rewards, setRewards] = useState<ClaimRewards | null>(null);
    const [request, setRequest] = useState<ClaimRequest | null>(null);
    const [review, setReview] = useState<ClaimReview | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [authorized, setAuthorized] = useState(false);
    const [rewardsRefresh, setRewardsRefresh] = useState(0);
    const flow = useMemo(() => new ClaimFlow(api, browserClaimStorage(api.scopeKey), new ClaimBrowserChain()), [api]);
    const generation = useRef(0);
    useEffect(() => {
        generation.current++;
        setRewards(null);
        setRequest(null);
        setReview(null);
        setAuthorized(false);
        setError(null);
        setBusy(false);
    }, [flow, address]);
    const wallet = useCallback((): ClaimWallet => {
        const current = walletRef.current;
        if (!current.ready || !current.signer || !current.address) throw new Error("Connect a wallet to claim rewards");
        const payer = String(current.address);
        const sign = async (method: "signMessage" | "signTransaction", bytes: Uint8Array): Promise<Uint8Array> => {
            if (String(walletRef.current.address) !== payer)
                throw new Error("Wallet changed. Reopen the claim review.");
            const member = current.signer as unknown as Record<string, unknown>;
            const fn = member[method];
            if (typeof fn !== "function") throw new Error("This wallet does not support the required signature");
            const result: unknown = await fn.call(current.signer, bytes);
            if (!(result instanceof Uint8Array)) throw new Error("Wallet returned an invalid signature");
            return result;
        };
        return {
            address: payer,
            signMessage: (bytes) => sign("signMessage", bytes),
            signTransaction: (bytes) => sign("signTransaction", bytes)
        };
    }, []);
    const run = useCallback(async (work: () => Promise<void>): Promise<void> => {
        const version = generation.current;
        setBusy(true);
        setError(null);
        try {
            await work();
        } catch (e) {
            if (version === generation.current)
                setError(
                    e && typeof e === "object" && "userMessage" in e && typeof e.userMessage === "string"
                        ? e.userMessage
                        : e instanceof Error
                          ? e.message
                          : "Claim could not be completed"
                );
        } finally {
            if (version === generation.current) setBusy(false);
        }
    }, []);
    const load = (): Promise<void> =>
        run(async () => {
            const version = generation.current;
            const recovered = await flow.recover(wallet());
            if (version !== generation.current) return;
            setAuthorized(true);
            setRequest(recovered);
            setRewardsRefresh((value) => value + 1);
        });
    const prepare = (): Promise<void> =>
        run(async () => {
            const version = generation.current;
            const next = await flow.prepare(wallet(), (value) => {
                if (version === generation.current) setRequest(value);
            });
            if (version !== generation.current) return;
            setAuthorized(true);
            setReview(next);
            setRequest(next.request);
        });
    const submit = (): Promise<void> =>
        run(async () => {
            if (!review) return;
            const version = generation.current;
            const next = await flow.submit(review, wallet(), (value) => {
                if (version === generation.current) setRequest(value);
            });
            if (version === generation.current) {
                setRequest(next);
                setReview(null);
            }
        });
    /**
     * One-press claim: prepare and submit in a single action, without
     * rendering the review in between. The review object is used
     * directly rather than read back from state, so nothing has to
     * re-render between the two legs.
     *
     * Every guard in `flow.submit` still runs (transaction unchanged,
     * blockhash valid, fee not increased), so skipping the review UI
     * drops the screen, not the checks. The wallet's own confirmation
     * dialog is what the user reviews.
     *
     * `ClaimPanel` keeps using prepare/submit separately; this is for
     * surfaces that want a single button.
     */
    const claim = (): Promise<void> =>
        run(async () => {
            const version = generation.current;
            const prepared = await flow.prepare(wallet(), (value) => {
                if (version === generation.current) setRequest(value);
            });
            if (version !== generation.current) return;
            setAuthorized(true);
            setRequest(prepared.request);
            const settled = await flow.submit(prepared, wallet(), (value) => {
                if (version === generation.current) setRequest(value);
            });
            if (version === generation.current) {
                setRequest(settled);
                setReview(null);
            }
        });
    const observing =
        request?.state !== "completed" &&
        request?.state !== "blocked" &&
        (request?.attempts.some((a) => !["paid", "expired_unexecuted", "superseded"].includes(a.state)) ?? false);
    // Keyed on the request's id, not the object: each poll replaces `request`,
    // which must not restart the interval.
    const claimRequestUuid = request?.claimRequestUuid;
    useEffect(() => {
        if (!authorized || !claimRequestUuid || !observing || busy) return;
        let stopped = false;
        let pending = false;
        let statusError: string | null = null;
        const timer = setInterval(() => {
            if (pending) return;
            pending = true;
            void api
                .status(claimRequestUuid)
                .then((next) => {
                    if (!stopped) {
                        setRequest(next);
                        const previousStatusError = statusError;
                        setError((current) =>
                            next.state === "completed" || current === previousStatusError ? null : current
                        );
                        statusError = null;
                    }
                })
                .catch((e) => {
                    if (!stopped) {
                        statusError = e instanceof Error ? e.message : "Could not refresh claim status";
                        setError(statusError);
                    }
                })
                .finally(() => {
                    pending = false;
                });
        }, 4000);
        return (): void => {
            stopped = true;
            clearInterval(timer);
        };
    }, [api, authorized, claimRequestUuid, observing, busy]);
    // Rewards can require an upstream lookup. They must not block status or
    // keep the signing action busy after the server has confirmed payment.
    const requestState = request?.state;
    useEffect(() => {
        if (!authorized) return;
        let stopped = false;
        void api
            .rewards()
            .then((balance) => {
                if (!stopped) setRewards(balance);
            })
            .catch(() => {
                if (!stopped) setRewards(null);
            });
        return (): void => {
            stopped = true;
        };
    }, [api, authorized, claimRequestUuid, requestState, rewardsRefresh]);
    return { rewards, request, review, busy, error, authorized, ready, load, prepare, submit, claim };
}
